/**
 * API Service pentru Nomenclatoare Externe HIS
 * =============================================
 * 
 * Gestionează comunicarea cu backend-ul pentru:
 * - Lista nomenclatoare disponibile
 * - CRUD proceduri externe
 * - Mapping-uri RIS ↔ HIS
 * - Statistici și sugestii
 */

import api from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface NomenclatorInfo {
  his_system: string;
  his_system_name: string | null;
  total_procedures: number;
  mapped_count: number;
  unmapped_count: number;
  validated_count: number;
  auto_mapped_count: number;
}

export interface ExternalProcedure {
  id: string;
  external_code: string;
  external_name: string;
  external_modality: string | null;
  external_class_id: string | null;
  external_class_name: string | null;
  is_active: boolean;
  imported_at: string | null;
  
  // Mapping info
  mapping_id: string | null;
  ris_segment_id: string | null;
  ris_segment_code: string | null;
  ris_segment_name: string | null;
  mapping_type: 'auto' | 'manual' | 'exact' | null;
  mapping_validated: boolean;
  confidence_score: number | null;
}

export interface ExternalProcedureListResponse {
  items: ExternalProcedure[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ExternalProcedureUpdate {
  external_code?: string;
  external_name?: string;
  external_modality?: string;
  external_class_id?: string;
  external_class_name?: string;
  is_active?: boolean;
}

export interface MappingSuggestion {
  segment_id: string;
  segment_code: string;
  segment_name: string;
  modality: string;
  similarity_score: number;
}

export interface NomenclatorStats {
  total_procedures: number;
  active_procedures: number;
  inactive_procedures: number;
  mapped_count: number;
  unmapped_count: number;
  validated_count: number;
  auto_mapped_count: number;
  manual_mapped_count: number;
  by_modality: Record<string, { total: number; mapped: number; unmapped: number }>;
  avg_confidence_score: number | null;
}

export interface RisSegment {
  id: string;
  code: string;
  display_name: string;
  modality: string;
}

export interface ProceduresFilter {
  page?: number;
  page_size?: number;
  search?: string;
  modality?: string;
  mapping_status?: 'all' | 'mapped' | 'unmapped' | 'validated' | 'auto';
  is_active?: boolean;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const BASE_URL = '/external-nomenclators';

/**
 * Listează toate nomenclatoarele HIS disponibile
 */
export const listNomenclators = async (): Promise<NomenclatorInfo[]> => {
  const response = await api.get<NomenclatorInfo[]>(BASE_URL);
  return response.data;
};

/**
 * Listează procedurile dintr-un nomenclator cu filtre și paginare
 */
export const listProcedures = async (
  hisSystem: string,
  filters: ProceduresFilter = {}
): Promise<ExternalProcedureListResponse> => {
  const params = new URLSearchParams();
  
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.page_size) params.append('page_size', filters.page_size.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.modality) params.append('modality', filters.modality);
  if (filters.mapping_status && filters.mapping_status !== 'all') {
    params.append('mapping_status', filters.mapping_status);
  }
  if (filters.is_active !== undefined) {
    params.append('is_active', filters.is_active.toString());
  }
  
  const response = await api.get<ExternalProcedureListResponse>(
    `${BASE_URL}/${hisSystem}/procedures?${params.toString()}`
  );
  return response.data;
};

/**
 * Obține detalii despre o procedură
 */
export const getProcedure = async (
  hisSystem: string,
  procedureId: string
): Promise<ExternalProcedure> => {
  const response = await api.get<ExternalProcedure>(
    `${BASE_URL}/${hisSystem}/procedures/${procedureId}`
  );
  return response.data;
};

/**
 * Actualizează o procedură externă
 */
export const updateProcedure = async (
  hisSystem: string,
  procedureId: string,
  data: ExternalProcedureUpdate
): Promise<ExternalProcedure> => {
  const response = await api.put<ExternalProcedure>(
    `${BASE_URL}/${hisSystem}/procedures/${procedureId}`,
    data
  );
  return response.data;
};

/**
 * Obține sugestii de mapping pentru o procedură
 */
export const getMappingSuggestions = async (
  hisSystem: string,
  procedureId: string,
  limit: number = 5
): Promise<MappingSuggestion[]> => {
  const response = await api.get<MappingSuggestion[]>(
    `${BASE_URL}/${hisSystem}/procedures/${procedureId}/suggestions?limit=${limit}`
  );
  return response.data;
};

/**
 * Creează un mapping între procedură și segment RIS
 */
export const createMapping = async (
  hisSystem: string,
  procedureId: string,
  segmentDefinitionId: string,
  notes?: string
): Promise<{ message: string; mapping_id: string }> => {
  const response = await api.post(
    `${BASE_URL}/${hisSystem}/procedures/${procedureId}/mapping`,
    { segment_definition_id: segmentDefinitionId, notes }
  );
  return response.data;
};

/**
 * Șterge mapping-ul unei proceduri
 */
export const deleteMapping = async (
  hisSystem: string,
  procedureId: string
): Promise<{ message: string }> => {
  const response = await api.delete(
    `${BASE_URL}/${hisSystem}/procedures/${procedureId}/mapping`
  );
  return response.data;
};

/**
 * Validează mapping-ul unei proceduri
 */
export const validateMapping = async (
  hisSystem: string,
  procedureId: string
): Promise<{ message: string }> => {
  const response = await api.post(
    `${BASE_URL}/${hisSystem}/procedures/${procedureId}/validate`
  );
  return response.data;
};

/**
 * Obține statistici pentru un nomenclator
 */
export const getNomenclatorStats = async (
  hisSystem: string
): Promise<NomenclatorStats> => {
  const response = await api.get<NomenclatorStats>(
    `${BASE_URL}/${hisSystem}/stats`
  );
  return response.data;
};

/**
 * Listează segmentele RIS disponibile pentru mapping
 */
export const listRisSegments = async (
  hisSystem: string,
  modality?: string,
  search?: string
): Promise<RisSegment[]> => {
  const params = new URLSearchParams();
  if (modality) params.append('modality', modality);
  if (search) params.append('search', search);
  
  const response = await api.get<RisSegment[]>(
    `${BASE_URL}/${hisSystem}/ris-segments?${params.toString()}`
  );
  return response.data;
};

// Export all as default object for convenience
export const externalNomenclatorsApi = {
  listNomenclators,
  listProcedures,
  getProcedure,
  updateProcedure,
  getMappingSuggestions,
  createMapping,
  deleteMapping,
  validateMapping,
  getNomenclatorStats,
  listRisSegments,
};

export default externalNomenclatorsApi;

